import { getItemDef } from "../items/db.js";
import type { ContainerItemDef, ItemDef, ItemInstance } from "../items/types.js";

/**
 * Inventory Tetris: rectangles in a rectangle. PURE and integer-only, so the
 * client's optimistic placement and the server's are the same computation. A
 * grid is a plain `{ w, h, items }` record, not a bitmap: grids are small,
 * serialized whole, and occupancy recomputed per query is cheaper than keeping
 * a cache honest.
 */

/** A rotation: 0 is as authored, 1 is a quarter turn (w and h swap). */
export type Rotation = 0 | 1;

/** Where something goes: a cell and a rotation. */
export interface GridPlacement {
  x: number;
  y: number;
  rot: Rotation;
}

/** An item and where it sits. `x`/`y` are the item's top-left cell. */
export interface PlacedItem {
  item: ItemInstance;
  x: number;
  y: number;
  rot: Rotation;
}

/** A rectangle of cells in a grid's own coordinates. Integers, `w`/`h` >= 1. */
export interface GridRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface GridState {
  w: number;
  h: number;
  items: PlacedItem[];
  /**
   * The POUCHES, when this grid has any: separate compartments inside the same
   * w × h field, and an item must fit WHOLLY inside one. ABSENT MEANS ONE ZONE
   * COVERING THE WHOLE FIELD — every pre-pouch grid has no `zones` key and
   * serializes as it always did; an explicit full-field zone is never written.
   * CONTENT, not state: `cloneGrid` shares the array, and the layout is stamped
   * from `ContainerItemDef.layout` at creation so a grid off the wire carries
   * its own shape.
   */
  zones?: readonly GridRect[];
}

export function createGrid(w: number, h: number): GridState {
  return { w, h, items: [] };
}

/**
 * The capacity grid of `def`, wearing its pouch layout — THE one way a
 * container's grid is minted. A bare `createGrid(def.gridW, def.gridH)` would
 * be a rig with its pouch walls knocked out.
 */
export function createContainerGrid(def: ContainerItemDef): GridState {
  const grid: GridState = { w: def.gridW, h: def.gridH, items: [] };
  if (def.layout !== undefined) grid.zones = def.layout;
  return grid;
}

/**
 * `grid` with `def`'s pouch layout stamped on it, items untouched — the repair
 * path for grids stored before their def grew a layout (the schema-9
 * migration). The key is REMOVED when the def has no layout. Placements are NOT
 * re-validated; the caller owns what no longer fits.
 */
export function withLayout(grid: GridState, def: ContainerItemDef): GridState {
  const out = cloneGrid(grid);
  if (def.layout === undefined) delete out.zones;
  else out.zones = def.layout;
  return out;
}

/**
 * Footprint of `def` at `rot`. Rotation 1 swaps the axes — the entire rotation
 * model; 180° equals 0° for a rectangle.
 */
export function footprint(def: ItemDef, rot: Rotation): { w: number; h: number } {
  return rot === 1 ? { w: def.h, h: def.w } : { w: def.w, h: def.h };
}

/**
 * Footprint of an item already in a grid. An unknown def is 1×1 rather than a
 * throw — recoverable inside the tick loop.
 */
function placedFootprint(p: PlacedItem): { w: number; h: number } {
  const def = getItemDef(p.item.defId);
  if (def === null) return { w: 1, h: 1 };
  return footprint(def, p.rot);
}

/**
 * Why a placement is illegal, or `null`. Split from `canPlace` so
 * `applyInventoryOp` can say "bounds" or "overlap". A POUCH WALL IS "bounds",
 * not a third reason: with pouches the container's bounds ARE the pouches, and
 * a new reason string would have to thread through `ops.ts`, the wire and every
 * client.
 */
export function placementProblem(
  grid: GridState,
  def: ItemDef,
  x: number,
  y: number,
  rot: Rotation,
  ignoreIid?: string,
): "bounds" | "overlap" | null {
  if (!Number.isInteger(x) || !Number.isInteger(y)) return "bounds";
  const f = footprint(def, rot);
  if (x < 0 || y < 0 || x + f.w > grid.w || y + f.h > grid.h) return "bounds";

  const zones = grid.zones;
  if (zones !== undefined) {
    let inside = false;
    for (const z of zones) {
      if (x < z.x || y < z.y || x + f.w > z.x + z.w || y + f.h > z.y + z.h) continue;
      inside = true;
      break;
    }
    if (!inside) return "bounds";
  }

  for (const p of grid.items) {
    if (ignoreIid !== undefined && p.item.iid === ignoreIid) continue;
    const pf = placedFootprint(p);
    // Half-open rectangles: [x, x+w) x [y, y+h).
    if (x + f.w <= p.x || p.x + pf.w <= x) continue;
    if (y + f.h <= p.y || p.y + pf.h <= y) continue;
    return "overlap";
  }
  return null;
}

/**
 * True when `def` fits at (x, y, rot). `ignoreIid` excludes one item from the
 * overlap test — what makes moving an item one cell legal.
 */
export function canPlace(
  grid: GridState,
  def: ItemDef,
  x: number,
  y: number,
  rot: Rotation,
  ignoreIid?: string,
): boolean {
  return placementProblem(grid, def, x, y, rot, ignoreIid) === null;
}

/**
 * First free spot for `def`, or `null`. DETERMINISM CONTRACT — both sides run
 * this and must land on the same cell: rotation 0 everywhere before rotation 1
 * anywhere; within a rotation, rows top to bottom, cells left to right. A
 * square item skips the second pass. Pouches change nothing here — the zone
 * test lives in `canPlace` — so an oblong item on a pouched rig simply goes in
 * turned on its side.
 */
export function findFreePlacement(grid: GridState, def: ItemDef): GridPlacement | null {
  const rots: Rotation[] = def.w === def.h ? [0] : [0, 1];
  for (const rot of rots) {
    const f = footprint(def, rot);
    for (let y = 0; y + f.h <= grid.h; y++) {
      for (let x = 0; x + f.w <= grid.w; x++) {
        if (canPlace(grid, def, x, y, rot)) return { x, y, rot };
      }
    }
  }
  return null;
}

/** The placement holding `iid`, or `null`. */
export function findItem(grid: GridState, iid: string): PlacedItem | null {
  for (const p of grid.items) {
    if (p.item.iid === iid) return p;
  }
  return null;
}

/**
 * A shallow-but-safe copy: new array, new placements, SHARED instances
 * (immutable) and shared `zones` (content off the def; the key is only written
 * when it was there).
 */
export function cloneGrid(grid: GridState): GridState {
  const out: GridState = {
    w: grid.w,
    h: grid.h,
    items: grid.items.map((p) => ({ item: p.item, x: p.x, y: p.y, rot: p.rot })),
  };
  if (grid.zones !== undefined) out.zones = grid.zones;
  return out;
}

/**
 * Cells this grid can actually hold: the sum of its pouches, or `w * h`
 * without. The DENOMINATOR of every "12 / 30" — cells outside every pouch are
 * dead. Zones never overlap (`validateItemDb`), so a plain sum is the area.
 */
export function gridCapacity(grid: GridState): number {
  const zones = grid.zones;
  if (zones === undefined) return grid.w * grid.h;
  let n = 0;
  for (const z of zones) n += z.w * z.h;
  return n;
}

/** Total occupied cells — what a "12 / 30" capacity readout is made of. */
export function usedCells(grid: GridState): number {
  let n = 0;
  for (const p of grid.items) {
    const f = placedFootprint(p);
    n += f.w * f.h;
  }
  return n;
}
